import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  User,
  Heart,
  Briefcase,
  Wrench,
  ChefHat,
  GraduationCap,
  Award,
  Users
} from "lucide-react";

const trainingAreas = [
  "Mandatory Compliance",
  "Annual Dementia Refresher",
  "Catheter Care",
  "Challenging Behaviour",
  "Malnutrition (MUST)",
  "Dysphagia",
  "Common Health Problems",
  "End of Life Care"
];

export const OurTeam = () => {
  return (
    <section id="team" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-14">
          <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-200">
            Our Team
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            The People Behind the Care
          </h2>
          <div className="w-24 h-1 bg-teal-500 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From our Registered Manager to our kitchen, admin and maintenance teams, every person
            at Barnston Court plays a vital role in making life here feel like home.
          </p>
        </div>

        {/* Registered Manager Highlight */}
        <div className="max-w-5xl mx-auto mb-14">
          <Card className="border-2 border-teal-200 shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 p-6 md:p-8 text-white">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="bg-white/15 backdrop-blur p-4 rounded-xl">
                  <User className="h-10 w-10" />
                </div>
                <div>
                  <Badge className="bg-white/20 text-white hover:bg-white/30 mb-2">
                    Our Management
                  </Badge>
                  <h3 className="text-2xl md:text-3xl font-bold">Harshani Daniel</h3>
                  <p className="text-teal-50 mt-1">Registered Manager</p>
                </div>
              </div>
            </div>
            <CardContent className="p-6 md:p-8 bg-white">
              <p className="text-gray-700 leading-relaxed">
                Harshani leads Barnston Court with years of hands-on experience in the care sector.
                She oversees every aspect of the home's day-to-day running, supported by a team of
                Senior Carers and Carers who deliver the close, personal attention our residents
                deserve.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Key Stats */}
        <div className="grid sm:grid-cols-3 gap-4 max-w-4xl mx-auto mb-14">
          <Card className="border-none shadow-md bg-gradient-to-br from-orange-50 to-orange-100">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-bold text-orange-600 mb-1">36</div>
              <div className="text-sm font-semibold text-gray-700">Dedicated staff members</div>
            </CardContent>
          </Card>
          <Card className="border-none shadow-md bg-gradient-to-br from-teal-50 to-teal-100">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-bold text-teal-600 mb-1">100+</div>
              <div className="text-sm font-semibold text-gray-700">
                Years of combined care experience
              </div>
            </CardContent>
          </Card>
          <Card className="border-none shadow-md bg-gradient-to-br from-amber-50 to-amber-100">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-bold text-amber-600 mb-1">80%</div>
              <div className="text-sm font-semibold text-gray-700">
                NVQ Level 2 qualified carers
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Team Groups Grid */}
        <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto mb-14">
          {/* Care Team */}
          <Card className="border-none shadow-lg bg-gradient-to-br from-teal-50 to-white hover:shadow-xl transition-all">
            <CardContent className="p-8">
              <div className="bg-white text-teal-600 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4 shadow">
                <Heart className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Care Team</h3>
              <p className="text-gray-700 leading-relaxed">
                Our 26-strong care team is the heart of Barnston Court. With more than 100 years of
                combined experience between them, they deliver compassionate, person-centred care
                around the clock. 80% are NVQ Level 2 qualified in Health &amp; Social Care.
              </p>
            </CardContent>
          </Card>

          {/* Admin & Head Office */}
          <Card className="border-none shadow-lg bg-gradient-to-br from-orange-50 to-white hover:shadow-xl transition-all">
            <CardContent className="p-8">
              <div className="bg-white text-orange-600 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4 shadow">
                <Briefcase className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Admin &amp; Head Office</h3>
              <p className="text-gray-700 leading-relaxed">
                Behind the scenes, our admin and head-office colleagues look after fees, resident
                personal allowances, supplier invoices and payroll. This keeps the business side
                running smoothly so the care team can focus fully on residents.
              </p>
            </CardContent>
          </Card>

          {/* Domestic & Maintenance */}
          <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-white hover:shadow-xl transition-all">
            <CardContent className="p-8">
              <div className="bg-white text-blue-600 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4 shadow">
                <Wrench className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Domestic &amp; Maintenance
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Our domestic and maintenance teams keep every corner of Barnston Court clean, safe
                and welcoming. They work to strict COSHH, HSE and IPC standards, so residents and
                visitors can always trust the environment around them.
              </p>
            </CardContent>
          </Card>

          {/* Kitchen Team */}
          <Card className="border-none shadow-lg bg-gradient-to-br from-rose-50 to-white hover:shadow-xl transition-all">
            <CardContent className="p-8">
              <div className="bg-white text-rose-600 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4 shadow">
                <ChefHat className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Kitchen Team</h3>
              <p className="text-gray-700 leading-relaxed">
                Our cooks prepare fresh meals three times a day, tailored to each resident's
                dietary requirements, whether medical, cultural, or simply personal preference.
                Good food, served with care, is something we take seriously.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Training Commitment */}
        <div className="max-w-6xl mx-auto">
          <Card className="border-2 border-orange-200 shadow-xl bg-gradient-to-r from-orange-50 via-white to-teal-50">
            <CardContent className="p-8 md:p-10">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center bg-orange-100 text-orange-700 p-4 rounded-full mb-4">
                  <GraduationCap className="h-8 w-8" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  Training That Goes Beyond the Basics
                </h3>
                <p className="text-gray-700 max-w-3xl mx-auto leading-relaxed">
                  Barnston Court invests in continuous, high-quality training for every team
                  member. On top of all mandatory qualifications, we deliver specialist courses to
                  build deeper expertise, and every team member retrains annually in dementia care
                  to keep our knowledge fresh and current.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
                {trainingAreas.map((area, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 hover:border-orange-200 transition-all"
                  >
                    <Award className="h-5 w-5 text-orange-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700 font-medium">{area}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Closing Statement */}
        <div className="max-w-4xl mx-auto text-center mt-12">
          <p className="text-lg text-gray-700 leading-relaxed italic">
            "Every role at Barnston Court, from management and nurses to cooks, housekeepers and
            maintenance, is essential to the warm, safe and dignified life our residents enjoy
            every day."
          </p>
        </div>
      </div>
    </section>
  );
};
